import React from 'react'
import PropTypes from 'prop-types'
import { Check, X } from 'lucide-react'
import './PasswordStrengthIndicator.css'

/**
 * Password Strength Indicator Component
 *
 * Visual feedback for password strength with requirement checklist.
 *
 * @param {Object} props - Component props
 * @param {string} props.password - Password to evaluate
 */
const PasswordStrengthIndicator = ({ password = '' }) => {
	const requirements = [
		{
			id: 'length',
			label: 'At least 8 characters',
			test: (pwd) => pwd.length >= 8,
		},
		{
			id: 'uppercase',
			label: 'One uppercase letter',
			test: (pwd) => /[A-Z]/.test(pwd),
		},
		{
			id: 'lowercase',
			label: 'One lowercase letter',
			test: (pwd) => /[a-z]/.test(pwd),
		},
		{
			id: 'number',
			label: 'One number',
			test: (pwd) => /[0-9]/.test(pwd),
		},
		{
			id: 'special',
			label: 'One special character',
			test: (pwd) => /[^A-Za-z0-9]/.test(pwd),
		},
	]

	const passedCount = requirements.filter((req) => req.test(password)).length
	const totalCount = requirements.length
	const percentage = (passedCount / totalCount) * 100

	// Determine strength level
	let strengthLevel = 'weak'
	let strengthLabel = 'Weak'
	if (passedCount === totalCount) {
		strengthLevel = 'strong'
		strengthLabel = 'Strong'
	} else if (passedCount >= 3) {
		strengthLevel = 'medium'
		strengthLabel = 'Medium'
	}

	return (
		<div className="password-strength">
			{/* Strength Bar */}
			<div className="password-strength__bar-container">
				<div
					className={`password-strength__bar password-strength__bar--${strengthLevel}`}
					style={{ width: `${percentage}%` }}
				/>
			</div>

			{/* Strength Label */}
			<div className="password-strength__header">
				<span className="password-strength__label">Password Strength:</span>
				<span className={`password-strength__value password-strength__value--${strengthLevel}`}>
					{strengthLabel}
				</span>
			</div>

			{/* Requirements List */}
			<ul className="password-strength__requirements">
				{requirements.map((req) => {
					const passed = req.test(password)
					return (
						<li
							key={req.id}
							className={`password-strength__requirement ${
								passed ? 'password-strength__requirement--passed' : ''
							}`}
						>
							{passed ? (
								<Check size={14} className="password-strength__icon password-strength__icon--success" />
							) : (
								<X size={14} className="password-strength__icon password-strength__icon--pending" />
							)}
							<span>{req.label}</span>
						</li>
					)
				})}
			</ul>
		</div>
	)
}

PasswordStrengthIndicator.propTypes = {
	password: PropTypes.string,
}

export default PasswordStrengthIndicator
